// Dashboard 7 — Individual User Drill-Down. Search a user, then see their
// profile, multi-window communication summary, and the full paginated history
// of every message sent to them — including the exact message text delivered.

import React, { useCallback, useEffect, useState } from "react";
import { Clock, Search, User as UserIcon, X } from "lucide-react";
import { Skeleton } from "../../../ui/skeleton";
import { Button } from "../../../ui/button";
import { StatCard } from "../../../Components/admin/reporting/primitives";
import { Panel } from "../../../Components/admin/reporting/controls";
import {
  searchEngagementUsers,
  getEngagementUser,
} from "../../../Services/adminEngagement";
import {
  ENGAGEMENT_STATE_LABELS,
  LIFE_PILLAR_LABELS,
  CONVERSION_TYPE_LABELS,
  ChannelBadge,
  StatusBadge,
  BoolDot,
  ErrorBox,
  EmptyState,
  fmtInt,
  labelFor,
  useEngagementData,
} from "./engagementShared";

const PAGE_SIZE = 50;

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
};

const SUMMARY_WINDOWS = [
  ["30d", "Last 30 days"],
  ["60d", "Last 60 days"],
  ["90d", "Last 90 days"],
  ["lifetime", "Lifetime"],
];

// ── Search box + results ─────────────────────────────────────────────────────
const UserSearch = ({ onSelect }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = async (e) => {
    e?.preventDefault();
    const query = q.trim();
    if (!query) return;
    setLoading(true);
    setError(null);
    try {
      const payload = await searchEngagementUsers(query, 20);
      setResults(payload.users || []);
    } catch (err) {
      setError(err?.response?.data?.message || "Search failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Panel title="Find a user">
      <form onSubmit={run} className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, or user ID…"
            className="w-full rounded-md border border-slate-300 bg-background py-2 pl-9 pr-3 text-sm shadow-sm focus:border-sky-400 focus:outline-none focus:ring-1 focus:ring-sky-300"
          />
        </div>
        <Button type="submit" size="sm" disabled={!q.trim() || loading}>
          {loading ? "Searching…" : "Search"}
        </Button>
      </form>

      {error ? <div className="mt-3"><ErrorBox>{error}</ErrorBox></div> : null}

      {results != null ? (
        results.length === 0 ? (
          <EmptyState>No users matched “{q.trim()}”.</EmptyState>
        ) : (
          <ul className="mt-3 divide-y rounded-md border">
            {results.map((u) => (
              <li key={u.user_id}>
                <button
                  type="button"
                  onClick={() => onSelect(u.user_id)}
                  className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-slate-50"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                    <UserIcon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">
                      {u.name || "Unnamed user"}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {u.email || u.user_id}
                    </span>
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    Joined {fmtDate(u.registration_date)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </Panel>
  );
};

// ── Selected-user profile ────────────────────────────────────────────────────
const UserProfile = ({ userId, onClear }) => {
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [userId]);

  const { data, loading, error } = useEngagementData(
    () => getEngagementUser(userId, { page, pageSize: PAGE_SIZE }),
    [userId, page]
  );

  const overview = data?.overview || {};
  const summary = data?.communication_summary || {};
  const history = data?.message_history || [];
  const totalMessages = data?.total_messages || 0;
  // The user's OWN timezone — message date/time are pre-formatted in it (§3.5).
  const userTz = data?.timezone || overview.timezone;
  const totalPages = Math.max(1, Math.ceil(totalMessages / PAGE_SIZE));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold tracking-tight">User profile</h2>
        <Button variant="outline" size="sm" onClick={onClear}>
          <X className="mr-1.5 h-3.5 w-3.5" />
          Back to search
        </Button>
      </div>

      {error ? <ErrorBox>{error}</ErrorBox> : null}

      {loading && !data ? (
        <Skeleton className="h-32 w-full" />
      ) : (
        <>
          {/* Overview */}
          <Panel title={overview.name || "User"}>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm md:grid-cols-4">
              <div>
                <div className="text-xs text-muted-foreground">Email</div>
                <div className="truncate font-medium">{overview.email || "—"}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Engagement state</div>
                <div className="font-medium">
                  {overview.engagement_state
                    ? labelFor(ENGAGEMENT_STATE_LABELS, overview.engagement_state)
                    : "—"}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Dominant pillar</div>
                <div className="font-medium">
                  {overview.dominant_life_pillar
                    ? labelFor(LIFE_PILLAR_LABELS, overview.dominant_life_pillar)
                    : "—"}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Total sessions</div>
                <div className="font-medium tabular-nums">
                  {fmtInt(overview.total_sessions)}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Registered</div>
                <div className="font-medium">{fmtDate(overview.registration_date)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Last session</div>
                <div className="font-medium">{fmtDate(overview.last_session_date)}</div>
              </div>
              <div className="md:col-span-2">
                <div className="text-xs text-muted-foreground">User ID</div>
                <div className="truncate font-mono text-xs">{overview.user_id || userId}</div>
              </div>
            </div>
          </Panel>

          {/* Communication summary */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            {SUMMARY_WINDOWS.map(([key, label]) => {
              const s = summary[key] || {};
              return (
                <StatCard
                  key={key}
                  label={label}
                  value={fmtInt(s.messages_sent)}
                  sub={`${fmtInt(s.opens)} opens · ${fmtInt(s.sessions_started)} sessions`}
                />
              );
            })}
          </div>

          {/* Message history */}
          <Panel
            title="Message history"
            right={
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                {userTz ? (
                  <>
                    <Clock className="h-3 w-3" />
                    Times in {userTz}
                    <span className="text-slate-300">·</span>
                  </>
                ) : null}
                {fmtInt(totalMessages)} messages · newest first
              </span>
            }
          >
            {history.length === 0 ? (
              <EmptyState>No messages on record for this user.</EmptyState>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-xs text-muted-foreground">
                        <th className="py-2 pr-3 font-medium">When</th>
                        <th className="py-2 px-3 font-medium">Channel</th>
                        <th className="py-2 px-3 font-medium">Message</th>
                        <th className="py-2 px-3 font-medium">Status</th>
                        <th className="py-2 px-3 text-center font-medium">Open</th>
                        <th className="py-2 px-3 text-center font-medium">Click</th>
                        <th className="py-2 px-3 text-center font-medium">Session</th>
                        <th className="py-2 px-3 font-medium">Converted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map((m, i) => (
                        <tr key={i} className="border-b align-top">
                          <td
                            className="py-2.5 pr-3 whitespace-nowrap"
                            title={m.send_time ? `UTC: ${m.send_time}` : undefined}
                          >
                            <div className="font-medium">{m.date || "—"}</div>
                            <div className="text-[11px] text-muted-foreground">
                              {m.time || ""}
                            </div>
                          </td>
                          <td className="py-2.5 px-3">
                            <ChannelBadge channel={m.channel} />
                          </td>
                          <td className="py-2.5 px-3" style={{ maxWidth: 360 }}>
                            <div className="font-medium">{m.message_name || "—"}</div>
                            {m.full_message_text ? (
                              <div className="mt-0.5 text-[12px] leading-snug text-muted-foreground">
                                {m.full_message_text}
                              </div>
                            ) : null}
                          </td>
                          <td className="py-2.5 px-3">
                            <StatusBadge status={m.delivery_status} />
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            <BoolDot value={m.opened} />
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            <BoolDot value={m.clicked} />
                          </td>
                          <td className="py-2.5 px-3 text-center">
                            <BoolDot value={m.session_started} />
                          </td>
                          <td className="py-2.5 px-3">
                            {m.converted ? (
                              <span className="text-xs font-medium text-emerald-700">
                                {m.conversion_type
                                  ? CONVERSION_TYPE_LABELS[m.conversion_type] ||
                                    m.conversion_type
                                  : "Yes"}
                              </span>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 ? (
                  <div className="mt-4 flex items-center justify-between border-t pt-3 text-xs">
                    <button
                      type="button"
                      disabled={page <= 1 || loading}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className="rounded-md border px-3 py-1 font-medium text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
                    >
                      ← Prev
                    </button>
                    <span className="text-muted-foreground">
                      Page {page} of {totalPages}
                    </span>
                    <button
                      type="button"
                      disabled={page >= totalPages || loading}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      className="rounded-md border px-3 py-1 font-medium text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
                    >
                      Next →
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </Panel>
        </>
      )}
    </div>
  );
};

const UserDrillDownTab = () => {
  const [userId, setUserId] = useState(null);
  const select = useCallback((id) => setUserId(id), []);
  return userId ? (
    <UserProfile userId={userId} onClear={() => setUserId(null)} />
  ) : (
    <UserSearch onSelect={select} />
  );
};

export default UserDrillDownTab;
